import moment from "moment";
import { useTranslation } from "react-i18next";

import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";
import { initialsOf } from "../../General/Cashflow.Dashboard.Functions";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import * as ComponentFunctions from './PaymentImputations.View.Function';
import PaymentImputationsComponent from '../PaymentImputations/PaymentImputations.Component';
import { GuideDrawer, GuideDrawerSection } from "../../../../theme/components/GuideDrawer";
import "../../CashFlow.Detail.Styles.css";

const fmt = (amount: number | undefined | null, currency: string | undefined) =>
    formatCurrencyAmountWithIssue(amount ?? 0, currency || 'ARS');

const Party = ({ role, name }: { role: string; name: string }) => (
    <div className="cfd-party">
        <span className={`cfd-avatar${name === MEDICAL_CENTER_NAME ? ' cfd-avatar--me' : ''}`} aria-hidden>{initialsOf(name)}</span>
        <div className="cfd-party__meta">
            <span className="cfd-party__role">{role}</span>
            <span className="cfd-party__name" title={name}>{name}</span>
        </div>
    </div>
);

/**
 * Detalle de un pago o cobro: cuánto fue, entre quiénes, cuánto ya está
 * aplicado a deudas y cuánto falta imputar; abajo, las imputaciones.
 */
const PaymentImputationsViewComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();

    if (!props.paymentId) return null;

    const payment = props.Payment;
    const kindLabel = props.IsReceived ? 'Cobro' : 'Pago';

    const renderSummary = () => {
        if (!payment) return null;

        const currency = payment.currencyCode || 'ARS';
        const amount = payment.amount ?? 0;
        const imputed = payment.imputedAmount ?? 0;
        const pending = payment.pendingIndictmentAmount ?? 0;
        const imputedPct = amount > 0 ? Math.min(100, (imputed / amount) * 100) : 0;

        let tag = <span className="cfd-tag cfd-tag--info">Imputado</span>;
        if (pending > 0) {
            tag = imputed > 0
                ? <span className="cfd-tag cfd-tag--partial">Parcialmente imputado</span>
                : <span className="cfd-tag cfd-tag--unimputed">Sin imputar</span>;
        }

        const extras = [
            { label: 'Importe de factura', value: payment.invoiceAmount },
            { label: 'Retenciones', value: payment.retentionAmount },
            { label: 'Gastos bancarios', value: payment.bankChargesAmount }
        ].filter((item) => (item.value ?? 0) > 0);

        return (
            <>
                <div className="cfd-hero">
                    <div className="cfd-hero__top">
                        <div>
                            <div className="cfd-hero__label">{kindLabel} del {payment.paymentDate ? moment(payment.paymentDate).format('DD/MM/YYYY') : '—'}</div>
                            <div className="cfd-hero__amount cfd-num">{fmt(amount, currency)}</div>
                        </div>
                        <div className="cfd-hero__tags">{tag}</div>
                    </div>
                    <div className="cfd-bar cfd-bar--imputed" role="progressbar" aria-label="Imputado sobre el total" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(imputedPct)}>
                        <span style={{ width: `${imputedPct}%` }} />
                    </div>
                    <div className="cfd-figures">
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Imputado · {imputedPct.toFixed(0)}%</div>
                            <div className="cfd-figure__value cfd-num">{fmt(imputed, currency)}</div>
                        </div>
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Sin imputar</div>
                            <div className={`cfd-figure__value cfd-num ${pending > 0 ? 'cfd-figure__value--pending' : 'cfd-figure__value--muted'}`}>
                                {pending > 0 ? fmt(pending, currency) : '—'}
                            </div>
                        </div>
                        <div className="cfd-figure">
                            <div className="cfd-figure__label">Fecha de imputación</div>
                            <div className="cfd-figure__value cfd-num">{payment.imputationDate ? moment(payment.imputationDate).format('DD/MM/YYYY') : '—'}</div>
                        </div>
                    </div>
                </div>

                <GuideDrawerSection title="Partes">
                    <div className="cfd-parties">
                        <Party role="Pagó" name={props.NameOf(payment.payer)} />
                        <span className="cfd-party__arrow" aria-hidden>→</span>
                        <Party role="Cobró" name={props.NameOf(payment.receiver)} />
                    </div>
                </GuideDrawerSection>

                {(extras.length > 0 || payment.comments) && (
                    <GuideDrawerSection title={`Datos del ${kindLabel.toLowerCase()}`}>
                        {extras.length > 0 && (
                            <dl className="cfd-rows">
                                {extras.map((item) => (
                                    <div key={item.label} className="cfd-row"><dt>{item.label}</dt><dd className="cfd-num">{fmt(item.value, currency)}</dd></div>
                                ))}
                            </dl>
                        )}
                        {payment.comments && <div className="cfd-comment">{payment.comments}</div>}
                    </GuideDrawerSection>
                )}
            </>
        );
    };

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title={payment ? `Detalle del ${kindLabel.toLowerCase()}` : (props.title ?? t('cashflow.imputations.viewTitle', 'Imputaciones del pago'))}
            eyebrow={`Flujo de caja · ${kindLabel} #${props.paymentId}`}
            onDismiss={props.onClose}
            closeLabel={t('common.close', 'Cerrar')}
            testId="payment-imputations-view-drawer"
        >
            <div className="cfd-root">
                {renderSummary()}
                <GuideDrawerSection>
                    <PaymentImputationsComponent paymentId={props.paymentId} />
                </GuideDrawerSection>
            </div>
        </GuideDrawer>
    );
};

export default ComponentFunctions.connector(PaymentImputationsViewComponent);
